/**
 * YouTube Player Manager - Handles multiple video players on the same page
 */
class YouTubePlayerManager {
	constructor() {
		this.players = new Map();
		this.apiLoaded = false;
		this.observers = new Set();
		this.init();
	}

	init() {
		// Load YouTube API if not already loaded
		if (!window.YT) {
			this.loadYouTubeAPI();
		} else {
			this.apiLoaded = true;
			this.initializePlayers();
		}

		// Set up mutation observer for dynamically added players
		this.setupMutationObserver();
	}

	loadYouTubeAPI() {
		// Check if script is already loading
		if (document.querySelector('script[src*="youtube.com/iframe_api"]')) {
			return;
		}

		const tag = document.createElement('script');
		tag.src = 'https://www.youtube.com/iframe_api';
		const firstScriptTag = document.getElementsByTagName('script')[0];
		firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);

		// Set global callback
		window.onYouTubeIframeAPIReady = () => {
			this.apiLoaded = true;
			this.initializePlayers();
		};
	}

	initializePlayers() {
		const playerElements = document.querySelectorAll('[data-video-id]');

		playerElements.forEach((element) => {
			const videoId = element.dataset.videoId;
			const playerId = element.id;

			if (videoId && playerId && !this.players.has(playerId)) {
				this.createPlayer(playerId, videoId, element.dataset);
			}
		});
	}

	createPlayer(playerId, videoId, options = {}) {
		try {
			// Default player configuration
			const defaultConfig = {
				height: '100%',
				width: '100%',
				videoId: videoId,
				playerVars: {
					playsinline: 1,
					mute: options.mute !== 'false' ? 1 : 0,
					autoplay: options.autoplay === 'true' ? 1 : 0,
					loop: options.loop === 'true' ? 1 : 0,
					controls: options.controls === 'false' ? 0 : 1,
					showinfo: 0,
					rel: 0,
					iv_load_policy: 3,
					modestbranding: 1,
					start: parseInt(options.start) || 0,
					end: parseInt(options.end) || 0,
				},
				events: {
					onReady: (event) => this.onPlayerReady(event, playerId),
					onStateChange: (event) =>
						this.onPlayerStateChange(event, playerId),
					onError: (event) => this.onPlayerError(event, playerId),
				},
			};

			const player = new YT.Player(playerId, defaultConfig);

			this.players.set(playerId, {
				player: player,
				videoId: videoId,
				options: options,
				element: document.getElementById(playerId),
			});
		} catch (error) {
			console.error('Error creating YouTube player:', error);
		}
	}

	onPlayerReady(event, playerId) {
		const playerData = this.players.get(playerId);
		if (playerData && playerData.options.autoplay === 'true') {
			event.target.playVideo();
		}

		// Trigger custom ready event
		this.dispatchCustomEvent('ytPlayerReady', {
			playerId: playerId,
			player: event.target,
		});
	}

	onPlayerStateChange(event, playerId) {
		const playerData = this.players.get(playerId);

		// Handle looping
		if (
			event.data === YT.PlayerState.ENDED &&
			playerData &&
			playerData.options.loop === 'true'
		) {
			event.target.playVideo();
		}

		// Trigger custom state change event
		this.dispatchCustomEvent('ytPlayerStateChange', {
			playerId: playerId,
			player: event.target,
			state: event.data,
		});
	}

	onPlayerError(event, playerId) {
		console.error(`YouTube player error for ${playerId}:`, event.data);

		// Trigger custom error event
		this.dispatchCustomEvent('ytPlayerError', {
			playerId: playerId,
			player: event.target,
			error: event.data,
		});
	}

	dispatchCustomEvent(eventName, detail) {
		const customEvent = new CustomEvent(eventName, { detail });
		document.dispatchEvent(customEvent);
	}

	setupMutationObserver() {
		if (typeof MutationObserver === 'undefined') return;

		const observer = new MutationObserver((mutations) => {
			mutations.forEach((mutation) => {
				mutation.addedNodes.forEach((node) => {
					if (node.nodeType === 1) {
						// Element node
						// Check if the node itself has video data
						if (node.dataset && node.dataset.videoId && node.id) {
							if (this.apiLoaded && !this.players.has(node.id)) {
								this.createPlayer(
									node.id,
									node.dataset.videoId,
									node.dataset
								);
							}
						}

						// Check child nodes
						const playerElements = node.querySelectorAll
							? node.querySelectorAll('[data-video-id]')
							: [];

						playerElements.forEach((element) => {
							const videoId = element.dataset.videoId;
							const playerId = element.id;

							if (
								videoId &&
								playerId &&
								this.apiLoaded &&
								!this.players.has(playerId)
							) {
								this.createPlayer(
									playerId,
									videoId,
									element.dataset
								);
							}
						});
					}
				});
			});
		});

		observer.observe(document.body, {
			childList: true,
			subtree: true,
		});

		this.observers.add(observer);
	}

	// Public API methods
	getPlayer(playerId) {
		const playerData = this.players.get(playerId);
		return playerData ? playerData.player : null;
	}

	playVideo(playerId) {
		const player = this.getPlayer(playerId);
		if (player && typeof player.playVideo === 'function') {
			player.playVideo();
		}
	}

	pauseVideo(playerId) {
		const player = this.getPlayer(playerId);
		if (player && typeof player.pauseVideo === 'function') {
			player.pauseVideo();
		}
	}

	stopVideo(playerId) {
		const player = this.getPlayer(playerId);
		if (player && typeof player.stopVideo === 'function') {
			player.stopVideo();
		}
	}

	stopAllVideos() {
		this.players.forEach((playerData, playerId) => {
			this.stopVideo(playerId);
		});
	}

	muteVideo(playerId) {
		const player = this.getPlayer(playerId);
		if (player && typeof player.mute === 'function') {
			player.mute();
		}
	}

	unmuteVideo(playerId) {
		const player = this.getPlayer(playerId);
		if (player && typeof player.unMute === 'function') {
			player.unMute();
		}
	}

	setVolume(playerId, volume) {
		const player = this.getPlayer(playerId);
		if (player && typeof player.setVolume === 'function') {
			player.setVolume(Math.max(0, Math.min(100, volume)));
		}
	}

	seekTo(playerId, seconds) {
		const player = this.getPlayer(playerId);
		if (player && typeof player.seekTo === 'function') {
			player.seekTo(seconds, true);
		}
	}

	getCurrentTime(playerId) {
		const player = this.getPlayer(playerId);
		if (player && typeof player.getCurrentTime === 'function') {
			return player.getCurrentTime();
		}
		return 0;
	}

	getDuration(playerId) {
		const player = this.getPlayer(playerId);
		if (player && typeof player.getDuration === 'function') {
			return player.getDuration();
		}
		return 0;
	}

	// Add new player dynamically
	addPlayer(playerId, videoId, options = {}) {
		if (this.apiLoaded) {
			this.createPlayer(playerId, videoId, options);
		} else {
			// Wait for API to load
			const checkAPI = setInterval(() => {
				if (this.apiLoaded) {
					this.createPlayer(playerId, videoId, options);
					clearInterval(checkAPI);
				}
			}, 100);
		}
	}

	// Remove player
	removePlayer(playerId) {
		const playerData = this.players.get(playerId);
		if (playerData && playerData.player) {
			if (typeof playerData.player.destroy === 'function') {
				playerData.player.destroy();
			}
			this.players.delete(playerId);
		}
	}

	// Get all players
	getAllPlayers() {
		const players = {};
		this.players.forEach((playerData, playerId) => {
			players[playerId] = playerData.player;
		});
		return players;
	}

	// Get player count
	getPlayerCount() {
		return this.players.size;
	}

	// Cleanup
	destroy() {
		// Stop all videos
		this.stopAllVideos();

		// Destroy all players
		this.players.forEach((playerData, playerId) => {
			if (
				playerData.player &&
				typeof playerData.player.destroy === 'function'
			) {
				playerData.player.destroy();
			}
		});

		// Disconnect observers
		this.observers.forEach((observer) => observer.disconnect());

		// Clear maps and sets
		this.players.clear();
		this.observers.clear();
	}
}

// Initialize the manager when DOM is ready
let youtubeManager;

function initYouTubeManager() {
	if (!youtubeManager) {
		youtubeManager = new YouTubePlayerManager();

		// Make it globally accessible
		window.youtubeManager = youtubeManager;
	}
	return youtubeManager;
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', initYouTubeManager);
} else {
	initYouTubeManager();
}

// Global functions for backward compatibility
window.getYouTubePlayer = function (playerId) {
	return youtubeManager ? youtubeManager.getPlayer(playerId) : null;
};

window.stopVideo = function (playerId = null) {
	if (youtubeManager) {
		if (playerId) {
			youtubeManager.stopVideo(playerId);
		} else {
			youtubeManager.stopAllVideos();
		}
	}
};

window.playVideo = function (playerId) {
	if (youtubeManager && playerId) {
		youtubeManager.playVideo(playerId);
	}
};

window.pauseVideo = function (playerId) {
	if (youtubeManager && playerId) {
		youtubeManager.pauseVideo(playerId);
	}
};

// Export for module use
export default YouTubePlayerManager;
